import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { TRPCClientError } from "@trpc/client"
import { trpc } from "#/lib/trpc/client"
import { trpcRetry } from "#/lib/trpc/retryPolicy"
import { useMemberProgramFlags } from "#/services/user/hooks/useMemberProgram"

/**
 * Exported so the pages a member pays on can invalidate the verdict they just changed.
 * Nothing else in the SPA writes this key: the consumer package keeps its own status query
 * under `["consumer", "subscription", "status"]` and owns its own tRPC client, so a payment
 * landing there is invisible here until someone says so.
 */
export const SUBSCRIPTION_STATUS_QUERY_KEY = ["GetSubscriptionStatus"] as const

export type SubscriptionStatus = Awaited<ReturnType<typeof trpc.subscription.getStatus.query>>

// The signed-in member's coarse Stripe subscription status (`none | unpaid | active | lapsed`)
// and the BFF's paywall verdict on it (`paywalled`).
// A protected procedure that resolves identity from the session, so it takes no
// argument; `enabled` is the caller's only lever, and the reason this hook exposes it
// rather than firing unconditionally — a member we already know isn't a consumer must
// not pay for a request whose answer can't change anything.
export function useSubscriptionStatus(enabled: boolean): UseQueryResult<SubscriptionStatus, TRPCClientError<never>> {
  return useQuery<SubscriptionStatus, TRPCClientError<never>, SubscriptionStatus, typeof SUBSCRIPTION_STATUS_QUERY_KEY>(
    {
      queryKey: SUBSCRIPTION_STATUS_QUERY_KEY,
      queryFn: () => trpc.subscription.getStatus.query(),
      // Refresh path for a member who is (or may be) walled: a payment that lands in another
      // tab, or a Stripe webhook that catches up while this tab sits idle, never reaches this
      // cache otherwise, and the wall would hold until a full page reload. Off once the member
      // is let through, so a paying member's tab costs nothing on every focus. Keyed on the
      // verdict, not `status`: an external-storefront member is always `none` to Stripe.
      refetchOnWindowFocus: (query) => query.state.data?.paywalled !== false,
      retry: trpcRetry(1),
      // Reported from the query rather than from each consumer's `useEffect`: on `/paywall` two
      // components read this hook, and a per-consumer effect turns one failure into two Sentry
      // events. `handleQueryError` skips 401s before it gets here, so a routine session expiry
      // no longer reports at all. (Auth failures on this procedure are UNAUTHORIZED by
      // construction — `protectedProcedure` is requireSession + requireCookie — so there is no
      // 403 to filter.)
      meta: {
        errorReport: {
          message: "Paywall: subscription.getStatus failed",
          tags: { feature: "paywall", query: "subscription.getStatus" }
        }
      },
      enabled
    }
  )
}

export type PaywallState = {
  isPaywalled: boolean
  /** No verdict yet: nothing cached to answer with, so nobody can be let through or held. */
  isLoading: boolean
  /**
   * A WALLED verdict is being re-read. Separate from `isLoading` because the two callers want
   * different things: the route gate must hold rather than act on a verdict a payment may have
   * just overturned, while the paywall page — already an allowed destination — must not blink
   * into a spinner every time the tab regains focus.
   */
  isRevalidating: boolean
  isError: boolean
}

// Whether the member should be held behind the paywall. The SPA's one reader of the BFF's
// verdict, so the route gate, the paywall page's reverse guard and the dev tools can never
// disagree. The rule itself — Stripe for the Carrot store, carrot-app's subscription state for an
// external storefront — is `isPaywalled` in @internal/constants, applied by `subscription.getStatus`;
// nothing here looks at a status.
//
// `enabled` is the caller's kill switch — false makes the hook inert and fires no request.
export function useIsPaywalled(enabled: boolean): PaywallState {
  const {
    data: programFlags,
    isInitialLoading: isProgramLoading,
    isError: isProgramError
  } = useMemberProgramFlags(enabled)

  // Chained deliberately: the status request waits for the program type and then only
  // fires for a consumer, so an enterprise or Engage member never issues it at all.
  const isConsumer = programFlags?.isConsumer === true
  const {
    data: subscription,
    isInitialLoading: isStatusLoading,
    isFetching: isStatusFetching,
    isError: isStatusError
  } = useSubscriptionStatus(enabled && isConsumer)

  const isError = isProgramError || isStatusError
  const hasWalledStatus = isConsumer && subscription?.paywalled === true

  return {
    // Fails open. The `isError` guard is not redundant with the undefined check: a
    // failed REFETCH leaves the last successful `data` in place, and a member must
    // never be walled on a status we can no longer confirm.
    isPaywalled: !isError && hasWalledStatus,
    // `isInitialLoading`, not `isLoading`: a disabled query reports `isLoading: true`
    // forever, which would hold a non-consumer on the loading state permanently.
    isLoading: isProgramLoading || isStatusLoading,
    // Only ever true over a walled verdict, so a member currently being let through is never
    // interrupted by a background re-read.
    isRevalidating: hasWalledStatus && isStatusFetching,
    isError
  }
}
